import { useEffect, useState } from "react";

/**
 * Subscribes to a CSS media query from JavaScript.
 *
 * Needed because the log list's row height is not purely a CSS concern: the virtualiser is told how
 * tall a row is so it can compute the scroll extent without measuring every row. A media query alone
 * can restyle the row, but the virtualiser would keep reserving the old height and the list would
 * scroll to the wrong place. So the breakpoint has to be readable from both sides.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === "undefined" ? false : window.matchMedia(query).matches,
  );

  useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent): void => setMatches(event.matches);
    // Set once on mount as well: the query may have changed between the initial state and the
    // effect running, for instance when a phone is rotated during load.
    setMatches(list.matches);
    list.addEventListener("change", onChange);
    return () => list.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/** The breakpoint below which the log list stacks onto two lines. Kept here so styles.css and the
 *  virtualiser cannot disagree about where it is. */
export const NARROW_QUERY = "(max-width: 680px)";
